import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { Num, Tag, Time, UserStatusTag, Uuid } from '../ui/values';
import { FilterSearch, useUrlFilter } from './common';
import { paths } from './paths';
import type { AdminUserRow } from './types';

/** Spielerliste (design.md §6, Revision R1): Spieler (MC-Name) · Status · Team · Kisten · zuletzt aktiv. */
export function PlayersPage() {
  const page = useKeysetPage();
  const [q, setQ] = useUrlFilter('q');
  const [status, setStatus] = useUrlFilter('status');
  const query = { q, status, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api.get<Page<AdminUserRow>>('/admin/users', query),
    placeholderData: keepPreviousData,
  });
  const filtered = Boolean(q || status);

  return (
    <div className="stack">
      <PageHead title="Spieler" context="Suche über Minecraft-Name, MC-UUID und (bei Admins) Discord" />
      <div className="filters">
        <FilterSearch label="Spieler suchen" value={q} onChange={setQ} />
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="active">aktiv</option>
          <option value="suspended">gesperrt</option>
        </select>
        {filtered && (
          <button
            type="button"
            className="btn-quiet"
            onClick={() => {
              setQ('');
              setStatus('');
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Spieler"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(u) => u.id}
        rowHref={(u) => paths.player(u.id)}
        empty={filtered ? 'Keine Spieler für diese Filter.' : 'Noch keine Spieler.'}
        columns={[
          {
            key: 'name',
            header: 'Spieler',
            cell: (u) => (
              <>
                {u.name ?? <span className="muted">ohne Namen</span>} {u.isAdmin && <Tag tone="neutral">Admin</Tag>}
              </>
            ),
          },
          { key: 'uuid', header: 'MC-UUID', hide: 'md', cell: (u) => (u.mcUuid ? <Uuid value={u.mcUuid} /> : <span className="muted">–</span>) },
          { key: 'status', header: 'Status', cell: (u) => <UserStatusTag status={u.status} /> },
          { key: 'team', header: 'Team', hide: 'sm', cell: (u) => u.teamName ?? <span className="muted">–</span> },
          { key: 'chests', header: 'Kisten', num: true, hide: 'md', cell: (u) => <Num value={u.chestCount} /> },
          { key: 'seen', header: 'zuletzt aktiv', hide: 'sm', cell: (u) => <Time iso={u.lastSeenAt} /> },
        ]}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </div>
  );
}
